import { ChevronRight, ExpandMore } from '@mui/icons-material'
import { CssBaseline, Zoom } from '@mui/material'
import { grey } from '@mui/material/colors'
import { ThemeProvider as MuiThemeProvider, alpha, createTheme } from '@mui/material/styles'
import { FC, PropsWithChildren } from 'react'
import type {} from '@mui/lab/themeAugmentation'
import type {} from '@mui/x-tree-view/themeAugmentation'

declare module '@mui/material/Chip' {
  interface ChipPropsSizeOverrides {
    tiny: true
  }
}

const theme = createTheme({
  components: {
    MuiAccordion: {
      defaultProps: {
        elevation: 0,
      },
      styleOverrides: {
        root: ({ theme }) => ({
          border: `2px solid ${theme.palette.grey[200]}`,
          '&:before': {
            content: 'none',
          },
        }),
      },
    },
    MuiAccordionDetails: {
      styleOverrides: {
        root: ({ theme }) => ({
          padding: important(theme.spacing(2)),
        }),
      },
    },
    MuiAccordionSummary: {
      defaultProps: {
        expandIcon: <ExpandMore fontSize="small" />,
      },
      styleOverrides: {
        root: ({ theme }) => ({
          borderTopLeftRadius: theme.spacing(1),
          borderTopRightRadius: theme.spacing(1),
          backgroundColor: theme.palette.grey[100],
        }),
      },
    },
    MuiAlert: {
      defaultProps: {
        variant: 'standard',
      },
      styleOverrides: {
        message: { fontWeight: 500 },
        standard: ({ theme }) => ({
          position: 'relative',
          overflow: 'hidden',
          borderRadius: theme.spacing(2.5),
        }),
        outlined: ({ theme }) => ({
          position: 'relative',
          overflow: 'hidden',
          borderRadius: theme.spacing(2.5),
        }),
      },
    },
    MuiAlertTitle: {
      styleOverrides: {
        root: { color: 'inherit', fontWeight: 600 },
      },
    },
    MuiAutocomplete: {
      defaultProps: {
        componentsProps: { paper: { elevation: 4 } },
        size: 'small',
      },
      styleOverrides: {
        inputRoot: ({ ownerState, theme }) => ({
          border: `2px solid ${theme.palette.grey[200]}`,
          '&.Mui-disabled': { borderColor: theme.palette.grey[300] },
          '&.Mui-focused': { borderColor: theme.palette.primary.light },
          ...(ownerState.size === 'small' &&
            ownerState.multiple &&
            (ownerState.value as any[]).length && {
              paddingTop: important(theme.spacing(1)),
              paddingBottom: important(theme.spacing(1)),
              '& .MuiInputBase-input': {
                paddingTop: important(0),
                paddingBottom: important(0),
              },
              '&:not(.Mui-focused) .MuiInputBase-input': {
                height: 0,
              },
            }),
        }),
        listbox: ({ theme }) => ({
          paddingLeft: theme.spacing(6 / 8),
          paddingRight: theme.spacing(6 / 8),

          '& .MuiListItem-root': {
            paddingLeft: theme.spacing(1.5),
            paddingRight: theme.spacing(1.5),
            borderRadius: theme.shape.borderRadius,

            '& .MuiListItemText-root': {
              fontWeight: 500,
              '& .MuiTypography-root': {
                fontWeight: 500,
              },
            },
          },

          '& .group-header': {
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            gap: theme.spacing(0.5),
            position: 'sticky',
            top: '-8px',
            zIndex: 9999,
            padding: theme.spacing(2, 2, 1),
            ...theme.typography.caption,
            color: theme.palette.grey[500],
            fontWeight: 500,
            backgroundColor: theme.palette.common.white,
            borderTop: `1px solid ${theme.palette.grey[200]}`,
          },
          '& li:first-of-type .group-header': {
            paddingTop: theme.spacing(0.5),
            borderTop: 'none',
          },
        }),
      },
    },
    MuiBadge: {
      defaultProps: {
        color: 'default',
      },
    },
    MuiButton: {
      defaultProps: {
        size: 'small',
      },
      styleOverrides: {
        sizeSmall: ({ theme }) => ({ borderRadius: theme.spacing(2), padding: '3px 18px' }),
        sizeMedium: ({ theme }) => ({ borderRadius: theme.spacing(3), padding: '5px 20px' }),
        sizeLarge: ({ theme }) => ({ borderRadius: theme.spacing(3) }),
        contained: ({ ownerState: { color }, theme }) => ({
          border: `2px solid ${
            color === 'inherit' ? theme.palette.grey[700] : theme.palette[color].main
          }`,
          boxShadow: 'none',
          // padding: '3px 18px',
          '&:hover': {
            borderColor: color === 'inherit' ? theme.palette.grey[700] : theme.palette[color].dark,
            borderWidth: 2,
            boxShadow: 'none',
          },
          '&.Mui-disabled': {
            borderColor: theme.palette.action.disabledBackground,
          },
        }),
        outlined: ({ ownerState: { color }, theme }) => ({
          borderWidth: 2,
          '&:hover': { borderWidth: 2 },
          '&.Mui-disabled': {
            borderColor:
              color === 'inherit' ? 'currentColor' : alpha(theme.palette[color].main, 0.25),
            borderWidth: 2,
            color: color === 'inherit' ? 'inherit' : alpha(theme.palette[color].main, 0.3),
          },
        }),
        root: ({ ownerState, theme }) => ({
          // borderRadius: theme.spacing(2),
          fontWeight: 600,
          textTransform: 'none',
          whiteSpace: 'nowrap',
          ...(ownerState.size === 'small' && { lineHeight: 1.65 }),
          ...(ownerState.variant === 'text' &&
            ownerState.color !== 'inherit' && {
              '&.Mui-hover': {
                backgroundColor: alpha(
                  theme.palette[ownerState.color].main,
                  theme.palette.action.hoverOpacity
                ),
              },
            }),
          ...(ownerState.variant === 'contained' &&
            ownerState.color !== 'inherit' && {
              '&.Mui-hover': {
                border: `1px solid ${theme.palette[ownerState.color].main}`,
                backgroundColor: alpha(
                  theme.palette[ownerState.color].main,
                  theme.palette.action.hoverOpacity
                ),
              },
            }),
          ...(ownerState.variant === 'outlined' &&
            ownerState.color !== 'inherit' && {
              '&:Mui-hover': {
                borderWidth: 2,
                boxShadow: 'none',
                backgroundColor: theme.palette[ownerState.color].dark,
              },
            }),
          '&:focus, &:active': ownerState.color !== 'inherit' && {
            boxShadow: important(`0 0 0 3px ${alpha(theme.palette[ownerState.color].main, 0.25)}`),
          },
        }),
        textSizeSmall: ({ theme }) => ({ padding: theme.spacing(0.5, 1.5), lineHeight: 1.55 }),
      },
    },
    MuiChip: {
      styleOverrides: {
        filled: ({ ownerState, theme }) => ({
          ...(ownerState.color === 'secondary' && { color: theme.palette.grey[800] }),
          ...(ownerState.onClick &&
            ownerState.color === 'default' && {
              '&:hover': {
                color: theme.palette.grey[800],
              },
            }),
        }),
        root: ({ ownerState, theme }) => ({
          fontWeight: ownerState.color === 'primary' ? 600 : 500,
          ...(ownerState.color === 'default' && {
            borderColor: theme.palette.grey[400],
            color: theme.palette.grey[600],
          }),
        }),
      },
      variants: [
        {
          props: { size: 'tiny' },
          style: ({ theme }) => ({
            fontSize: '12px',
            height: 16,
            '& .MuiChip-label': { padding: theme.spacing(0, 6 / 8) },
          }),
        },
      ],
    },
    MuiDialogContent: {
      styleOverrides: {
        root: ({ theme }) => ({ padding: important(theme.spacing(2)) }),
      },
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: ({ theme }) => ({
          border: `2px solid ${theme.palette.grey[200]}`,
          padding: theme.spacing(2),
        }),
      },
    },
    MuiFormHelperText: {
      defaultProps: {
        // @ts-ignore
        component: 'div',
      },
      styleOverrides: {
        root: ({ ownerState, theme }) => ({
          margin: 0,
          marginLeft: 2,
          ...(!ownerState.error && { color: theme.palette.grey[500] }),
        }),
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: ({ ownerState, theme }) => ({
          '&:focus, &:active': {
            boxShadow: `0 0 0 3px ${
              ownerState.color === 'inherit'
                ? 'inherit'
                : alpha(
                    theme.palette[ownerState.color]?.main ??
                      { default: theme.palette.grey[700] }[ownerState.color],
                    0.25
                  )
            }`,
          },
        }),
      },
    },
    MuiInputBase: {
      styleOverrides: {
        input: { fontWeight: 500 },
        multiline: ({ ownerState }) =>
          ownerState.hiddenLabel && { paddingBottom: '0 !important', paddingTop: '0 !important' },
        root: ({ theme }) => ({
          color: theme.palette.grey[600],
          ...theme.typography.body2,
        }),
      },
    },
    MuiInputLabel: {
      defaultProps: {
        size: 'small',
        variant: 'filled',
      },
      styleOverrides: {
        root: ({ ownerState, theme }) => ({
          position: 'relative',
          color: theme.palette.grey[600],
          ...(ownerState.size === 'small' && { fontSize: theme.typography.caption.fontSize }),
          ...(ownerState.size === 'normal' && { fontSize: theme.typography.body2.fontSize }),
          '&[data-shrink=false]+.MuiInputBase-formControl .MuiInputBase-input::placeholder': {
            color: theme.palette.grey[400],
            opacity: important(1),
          },
        }),
      },
    },
    MuiLink: {
      styleOverrides: {
        root: ({ theme }) => ({
          textDecoration: 'none',
          fontSize: theme.typography.body2.fontSize,
        }),
      },
    },
    MuiList: {
      defaultProps: {
        dense: true,
        disablePadding: true,
      },
      styleOverrides: {
        root: ({ theme }) => ({
          padding: theme.spacing(1, 6 / 8),
          overflow: 'auto',

          '& .MuiListItem-root, & .MuiMenuItem-root': {
            paddingLeft: theme.spacing(1.5),
            paddingRight: theme.spacing(1.5),
            borderRadius: theme.shape.borderRadius,
          },

          '& .MuiListItem-root:not(:last-of-type), .MuiMenuItem-root:not(:last-of-type)': {
            marginBottom: theme.spacing(0.5),
          },

          '& .MuiListItem-root.dp-fadebg': {
            animation: 'fade-background 5s forwards',
            backgroundColor: alpha(theme.palette.primary.main, 0.1),
          },
          '@keyframes fade-background': {
            from: {
              backgroundColor: alpha(theme.palette.primary.main, 0.1),
            },
            to: {
              backgroundColor: 'transparent',
            },
          },
        }),
      },
    },
    MuiListItem: {
      styleOverrides: {
        root: ({ theme }) => ({
          gap: theme.spacing(2),
          '&:hover:not(.Mui-selected)': {
            backgroundColor: theme.palette.grey[50],
          },
          '&:not(.MuiAutocomplete-option)': {
            borderRadius: theme.spacing(1),
          },
        }),
      },
    },
    MuiListItemAvatar: {
      styleOverrides: {
        root: {
          minWidth: 0,
        },
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: ({ theme }) => ({ borderRadius: theme.shape.borderRadius }),
      },
    },
    MuiListItemIcon: {
      styleOverrides: {
        root: ({ theme }) => ({ minWidth: theme.spacing(3) }),
      },
    },
    MuiListItemText: {
      defaultProps: {
        primaryTypographyProps: { variant: 'body2' },
        secondaryTypographyProps: { variant: 'caption' },
      },
      styleOverrides: {
        secondary: ({ theme }) => ({ color: theme.palette.grey[500] }),
      },
    },
    MuiFilledInput: {
      defaultProps: {
        disableUnderline: true,
      },
      styleOverrides: {
        root: ({ ownerState, theme }) => ({
          color: theme.palette.grey[600],
          backgroundColor: theme.palette.grey[50],
          borderWidth: `2px !important`,
          borderStyle: 'solid',
          borderColor: theme.palette.grey[200],
          borderRadius: theme.spacing(1),
          '&:hover': {
            backgroundColor: 'white',
          },
          '&.Mui-focused': {
            backgroundColor: 'white',
            borderColor: theme.palette.primary.light,
          },
          ...(ownerState.disabled && { pointerEvents: 'none' }),
          ...(ownerState.size === 'medium' && { color: theme.palette.grey[700] }),
        }),
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: ({ ownerState, theme }) => ({
          paddingTop: theme.spacing(6 / 8),
          paddingBottom: theme.spacing(6 / 8),
          ...(ownerState.selected && {
            '&:hover': { backgroundColor: alpha(theme.palette.primary.main, 0.25) },
          }),
        }),
      },
    },
    MuiPaginationItem: {
      styleOverrides: {
        root: ({ theme }) => ({
          color: theme.palette.grey[700],
        }),
      },
    },
    MuiPopover: {
      styleOverrides: {
        root: { zIndex: 9999 },
      },
    },
    MuiSelect: {
      defaultProps: {
        size: 'small',
        variant: 'filled',
      },
      styleOverrides: {
        icon: { fontSize: '1.4rem' },
        filled: ({ theme }) => ({
          color: theme.palette.grey[600],
          '&:focus': { backgroundColor: 'transparent' },
        }),
      },
    },
    MuiSkeleton: {
      styleOverrides: {
        root: ({ ownerState, theme }) => ({
          ...(ownerState.variant !== 'circular' && { borderRadius: theme.spacing(1) }),
          transform: 'none',
        }),
      },
    },
    MuiSwitch: {
      styleOverrides: {
        root: ({ theme }) => ({
          width: 48,
          height: 24,
          padding: 0,

          '& .MuiSwitch-switchBase': {
            padding: 2,

            '&.Mui-checked': {
              color: '#fff',
              transform: 'translateX(24px)',

              '& .MuiSwitch-thumb:before': {
                // eslint-disable-next-line max-len
                backgroundImage: `url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" height="16" width="16" viewBox="0 0 24 24"><path fill="${encodeURIComponent(
                  theme.palette.primary.main
                )}" d="M21,7L9,19L3.5,13.5L4.91,12.09L9,16.17L19.59,5.59L21,7Z"/></svg>')`,
              },

              '& + .MuiSwitch-track': {
                opacity: 1,
                backgroundColor: theme.palette.primary.main,
              },
            },
          },

          '& .MuiSwitch-thumb': {
            width: 20,
            height: 20,
            '&:before': {
              content: "''",
              position: 'absolute',
              width: '100%',
              height: '100%',
              left: 0,
              top: 0,
              backgroundRepeat: 'no-repeat',
              backgroundPosition: 'center',
            },
          },

          '& .MuiSwitch-track': {
            opacity: 1,
            backgroundColor: theme.palette.grey[200],
            borderRadius: 14,
          },
        }),
      },
    },
    MuiSvgIcon: {
      defaultProps: {
        fontSize: 'small',
      },
    },
    MuiTab: {
      styleOverrides: {
        root: ({ theme }) => ({
          borderRadius: theme.spacing(1),
          minHeight: theme.spacing(4),
          marginRight: theme.spacing(0.5),
          padding: theme.spacing(6 / 8, 1.5),
          fontWeight: 600,
          textTransform: 'none',
          '&:hover, &.Mui-selected': {
            backgroundColor: theme.palette.grey[100],
          },
        }),
      },
    },
    MuiTableCell: {
      styleOverrides: {
        // @ts-ignore
        alignCenter: { textAlign: 'center !important' },
        // @ts-ignore
        alignJustify: { textAlign: 'justify !important' },
        // @ts-ignore
        alignLeft: { textAlign: 'left !important' },
        // @ts-ignore
        alignRight: { textAlign: 'right !important ' },
        head: ({ theme }) => ({
          color: theme.palette.grey[500],
          fontWeight: 400,
        }),
        root: ({ theme }) => ({ color: theme.palette.grey[700] }),
      },
    },
    MuiTabs: {
      styleOverrides: {
        flexContainer: ({ theme }) => ({ padding: theme.spacing(0.5, 0.5, 6 / 8, 0.5) }),
        root: ({ theme }) => ({
          borderBottom: `1px solid ${theme.palette.grey[200]}`,
          minHeight: theme.spacing(5.5),
        }),
        scrollButtons: ({ theme }) => ({
          borderRadius: theme.spacing(1),
          marginTop: theme.spacing(0.5),
          marginBottom: theme.spacing(6 / 8),
          '&:hover': {
            backgroundColor: theme.palette.grey[100],
          },
        }),
      },
    },
    MuiTextField: {
      defaultProps: {
        size: 'small',
        variant: 'filled',
      },
      styleOverrides: {
        root: ({ ownerState: { InputProps, size }, theme }) => ({
          ...(size === 'small' &&
            InputProps?.startAdornment && {
              '& .MuiInputAdornment-positionStart': {
                marginTop: important(0),
              },
            }),
          ...(size === 'medium' &&
            InputProps?.startAdornment && {
              '& .MuiInputLabel-root': {
                fontSize: '1.2rem',
                '&.Mui-focused, &.MuiInputLabel-hasValue': {
                  fontSize: theme.typography.body2.fontSize,
                },
              },
              '& .MuiInputAdornment-positionStart': {
                marginTop: important(0),
              },
            }),
        }),
      },
    },
    MuiFormControl: {
      defaultProps: {
        size: 'small',
        variant: 'filled',
      },
      styleOverrides: {
        root: ({ ownerState, theme }) => ({
          '& .MuiInputLabel-root': {
            transform: 'none',
            color: theme.palette.grey[600],
            marginLeft: theme.spacing(0.5),
            '&.Mui-focused': {
              color: theme.palette.primary.main,
            },
            '&.Mui-error': {
              color: theme.palette.error.main,
            },
          },

          // Small size
          '& .MuiInputBase-root.MuiInputBase-sizeSmall': {
            '&.MuiInputBase-multiline, &:not(.MuiInputBase-multiline) .MuiInputBase-input': {
              paddingTop: 8,
              paddingBottom: 8,
            },
            '&.MuiInputBase-multiline .MuiInputBase-input': {
              paddingTop: 0,
              paddingBottom: 0,
            },
            '&.MuiAutocomplete-inputRoot': {
              paddingTop: 0,
              paddingBottom: 0,
            },
          },

          // Size medium
          ...(ownerState.size === 'medium' && {
            '& .MuiInputBase-input': {
              fontSize: '1.2rem',
              fontWeight: 500,
              '&.MuiInputBase-inputHiddenLabel': {
                paddingTop: 8,
                paddingBottom: 9,
              },
              '&:not(.MuiInputBase-inputHiddenLabel)': {
                paddingTop: 8,
              },
            },
          }),
        }),
      },
    },
    MuiTooltip: {
      defaultProps: {
        arrow: true,
        enterDelay: 600,
        placement: 'top',
        TransitionComponent: Zoom,
      },
      styleOverrides: {
        tooltip: ({ theme }) => ({ borderRadius: theme.spacing(1) }),
        tooltipPlacementTop: { marginBottom: '10px !important' },
      },
    },
    MuiTreeItem: {
      styleOverrides: {
        content: ({ theme }) => ({
          '&.Mui-expanded': {
            '& .MuiTreeItem-label': {
              color: theme.palette.grey[700],
              fontWeight: 500,
            },
            '& .MuiTreeItem-iconContainer': {
              color: theme.palette.grey[600],
            },
          },
          '&:not(.Mui-expanded)': {
            '& .MuiTreeItem-label': {
              color: theme.palette.grey[500],
              fontWeight: 400,
            },
            '& .MuiTreeItem-iconContainer': {
              color: theme.palette.grey[400],
            },
          },
          '&.Mui-focused': {
            backgroundColor: 'transparent',
          },
          '&.Mui-selected > .MuiTreeItem-label': {
            color: theme.palette.grey[700],
            fontWeight: 500,
          },
          '&.Mui-selected:has(+ div > .MuiTreeItem-group)': {
            backgroundColor: 'transparent',
          },
        }),
        group: ({ theme }) => ({ borderLeft: `2px dotted ${theme.palette.grey[200]}` }),
        label: ({ theme }) => theme.typography.body2,
      },
    },
    MuiTreeView: {
      defaultProps: {
        defaultCollapseIcon: <ExpandMore />,
        defaultExpandIcon: <ChevronRight />,
      },
    },
    MuiTypography: {
      defaultProps: { variant: 'body2' },
      styleOverrides: {
        caption: { lineHeight: 'normal' },
      },
    },
  },
  palette: {
    primary: {
      light: '#7789D8',
      main: '#485fc7',
      dark: '#394C9F',
    },
    secondary: {
      contrastText: '#fff',
      light: '#74E8D7',
      main: '#00d1b2',
      dark: '#00A78E',
    },
    error: {
      light: '#ff5f7f',
      main: '#ff3860',
      dark: '#b22743',
    },
  },
  shape: {
    borderRadius: 8,
  },
  typography: {
    allVariants: {
      color: grey[700],
      fontWeight: 500,
      letterSpacing: 'normal',
    },
    fontFamily: '"Inter", Helvetica, Arial, sans serif',
  },
})

interface ThemeProviderProps {}

const ThemeProvider: FC<PropsWithChildren<ThemeProviderProps>> = ({ children }) => (
  <MuiThemeProvider theme={theme}>
    <CssBaseline>{children}</CssBaseline>
  </MuiThemeProvider>
)

const important = (value: string | number) => `${value} !important`

export default ThemeProvider
